import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getGuestId, checkGuestInMySQL } from "../utils/guest";
import { useAuth } from "../context/AuthContext"; // ✅ Ensures signed-up users can access

const ProtectedRoute = () => {
  const { user, guest, firebaseInitialized, loading } = useAuth(); // ✅ Fetch auth state
  const [accessGranted, setAccessGranted] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const location = useLocation();

  useEffect(() => {
    async function verifyAccess() {
      // Wait for Firebase to load before checking access
      if (!firebaseInitialized) {
        return;
      }

      const currentPath = location.pathname;
      
      // Routes that require authentication only (no guests allowed)
      const authOnlyRoutes = ['/profile', '/look-list', '/shop-the-avatar'];
      
      // Routes that require credits (signed in user OR guest with credits)
      const creditRequiredRoutes = [];

      if (authOnlyRoutes.includes(currentPath)) {
        // ✅ Authentication-only routes: Must be signed in (no guests)
        if (user) {
          setAccessGranted(true);
        } else {
          setAccessGranted(false);
        }
      } else if (creditRequiredRoutes.includes(currentPath)) {
        // ✅ Credit-required routes: Must have credits (signed in user OR guest with credits)
        if (user) {
          // Signed in user - grant access (assume they have credits)
          setAccessGranted(true);
        } else if (guest) {
          // Guest user - check if they have credits
          if (guest.credits > 0) {
            setAccessGranted(true);
          } else {
            setAccessGranted(false);
          }
        } else {
          // No user or guest - check if we can create a guest with credits
          try {
            const guestId = await getGuestId();
            const guestData = await checkGuestInMySQL(guestId);
            
            if (guestData?.credits > 0) {
              setAccessGranted(true);
            } else {
              setAccessGranted(false);
            }
          } catch (error) {
            console.error("Error checking guest access:", error);
            setAccessGranted(false);
          }
        }
      } else {
        // ✅ Default: deny access for unknown routes
        setAccessGranted(false);
      }
      
      setCheckingAccess(false);
    }

    verifyAccess();
  }, [user, guest, firebaseInitialized, location.pathname]); // ✅ Runs when auth state or route changes

  // Show loading while Firebase is loading or checking access
  if (loading || checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-center text-gray-400">🔄 Checking access...</p>
      </div>
    );
  }

  // If Firebase is not initialized yet, show loading
  if (!firebaseInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-center text-gray-400">🔄 Initializing...</p>
      </div>
    );
  }

  // Grant access based on route requirements:
  // - /profile, /look-list, /shop-the-avatar: Must be signed in (no guests)
  // - No credit-required routes currently defined
  return accessGranted ? <Outlet /> : <Navigate to="/signup" />;
};

export default ProtectedRoute;
